import { getUserName } from '../../auth';
export class ChatbotService {
    client;
    dispatcher;
    formKey;
    chatHistory = [];
    isLoading = false;
    threadId;
    chatPath;
    userName;
    constructor(client, dispatcher, formKey, threadId, chatPath) {
        this.client = client;
        this.dispatcher = dispatcher;
        this.formKey = formKey;
        this.threadId = threadId;
        this.chatPath = chatPath;
        this.userName = getUserName();
    }
    setChatPath(chatPath) {
        this.chatPath = chatPath;
    }
    setChatHistory(messages) {
        this.chatHistory = messages || [];
    }
    setThreadId(threadId) {
        this.threadId = threadId;
    }
    setFormKey(formKey) {
        this.formKey = formKey;
    }
    getMessages() {
        let messages = [...this.chatHistory];
        return messages.filter(message => !message.text?.startsWith('$['));
    }
    lastMessageIsContinueCommand() {
        const lastMessage = this.chatHistory[this.chatHistory.length - 1];
        const lastMessageIsContinueCommand = lastMessage?.text?.startsWith('$[continue]');
        return lastMessageIsContinueCommand;
    }
    lastMessageIsFromBot() {
        const lastMessage = this.chatHistory[this.chatHistory.length - 1];
        const lastMessageIsFromBot = lastMessage?.role === 'assistant';
        return lastMessageIsFromBot;
    }
    continueFillingCommand() {
        this.dispatch('sendChatMessage', { text: '$[continue]' });
    }
    addMessage(message) {
        this.chatHistory.push(message);
    }
    addUserMessage(message) {
        this.addMessage({ role: "user", text: message });
    }
    addBotMessage(message) {
        this.addMessage({ role: "assistant", text: message, messageType: "answer" });
    }
    processConversationResponse(response) {
        this.addMessage({ role: "assistant",
            messageType: response.messageType,
            messageFields: response.messageFields,
            data: response.data,
            text: response.text,
            messageData: response?.messageData
        });
        // Handle different message types
        switch (response.messageType) {
            case 'select':
                // Handle selection responses (form selection, etc.)
                if (response.data?.selectOptions) {
                }
                break;
            case 'question':
                // Handle multi-field questions
                if (response.messageFields && response.messageFields.length > 0) {
                }
                break;
            case 'success':
                // Handle successful completion
                console.log('Form completed successfully:', response.data);
                break;
            case 'validation':
                // Handle validation errors
                console.log('Validation error for field:', response.messageFields);
                break;
            default:
                // Handle regular answers
                break;
        }
    }
    dispatch(action, ...args) {
        this.dispatcher(`chatbotService.${action}`, ...args);
    }
    async askFormQuestion(question, fieldId) {
        this.isLoading = true;
        let userQuestion = { role: "user", text: question };
        this.addMessage(userQuestion);
        try {
            let response = await this.client.askFormQuestion(this.formKey, fieldId, question, this.getMessages().map(message => message.text), this.threadId);
            let botMessage = { role: "assistant", messageType: "answer", text: response };
            this.addMessage(botMessage);
            this.dispatch("messageReceived", botMessage);
            return response;
        }
        catch (error) {
            this.dispatch("messageError", error);
        }
        finally {
            this.isLoading = false;
        }
    }
    async addInitialMessage(message, autoStart = true) {
        this.isLoading = true;
        try {
            if (message && !autoStart) {
                // autoStart off: show initial message as standalone bubble, wait for user
                this.addMessage({ role: "assistant", messageType: "answer", text: message });
            }
            if (autoStart) {
                // autoStart on: backend merges initialMessage (if any) into first AI response
                await this.continueFillingCommand();
            }
        }
        catch (error) {
            this.dispatch("messageError", error);
        }
        finally {
            this.isLoading = false;
        }
    }
    async sendChatMessage({ text, currentData, role }) {
        const userMessage = { role: role || "user", text, data: currentData };
        this.addMessage(userMessage);
        this.dispatch("messageSent", userMessage);
        this.isLoading = true;
        try {
            // Call the chat endpoint
            console.log("Sending chat message to form key", this.formKey, "threadId", this.threadId, "chatPath", this.chatPath);
            const response = await this.client.sendChatMessage(this.formKey, {
                text,
                threadId: this.threadId,
                userName: this.userName,
                externalIntegrationId: 'help-context',
                currentData,
                appendOnly: false,
                role
            }, this.chatPath);
            this.isLoading = false;
            this.processConversationResponse(response);
            this.dispatch("messageReceived", response);
        }
        catch (error) {
            // @TODO: FLU-193 Handle this.
            let text = error?.response?.data?.message || 'Sorry, I encountered an error while processing your message. Please try again.';
            this.addMessage({
                role: "assistant",
                messageType: 'error',
                text: text
            });
            this.isLoading = false;
            this.dispatch("messageError", error);
        }
        finally {
            return this.chatHistory[this.chatHistory.length - 1];
        }
    }
    async appendMessage({ text, role }) {
        this.addMessage({ role: role || "user", text });
        try {
            // Call the chat endpoint
            await this.client.sendChatMessage(this.formKey, {
                text,
                threadId: this.threadId,
                userName: this.userName,
                externalIntegrationId: 'help-context',
                appendOnly: true,
                role
            }, this.chatPath);
        }
        catch (error) {
            this.dispatch("messageError", error);
        }
    }
    messageSent(message) { }
    messageReceived(message) { }
    messageError(error) { }
    async handleFormSubmission(formKey, formData) {
        this.isLoading = true;
        try {
            // Send form data as a structured message
            const formDataText = Object.entries(formData)
                .map(([key, value]) => `${key}: ${value}`)
                .join(', ');
            const response = await this.client.sendChatMessage(formKey, {
                text: `Form submission: ${formDataText}`,
                threadId: this.threadId,
                userName: this.userName,
                externalIntegrationId: 'help-context'
            });
            this.processConversationResponse(response);
        }
        catch (error) {
            this.addMessage({
                role: "assistant",
                messageType: 'error',
                text: 'Sorry, I encountered an error while processing your form submission.'
            });
        }
        finally {
            this.isLoading = false;
        }
    }
    async handleSelection(formKey, selection) {
        this.isLoading = true;
        try {
            const response = await this.client.sendChatMessage(formKey, {
                text: selection,
                threadId: this.threadId,
                userName: this.userName,
                externalIntegrationId: 'help-context'
            });
            this.processConversationResponse(response);
        }
        catch (error) {
            this.addMessage({
                role: 'assistant',
                messageType: 'error',
                text: 'Sorry, I encountered an error while processing your selection.'
            });
        }
        finally {
            this.isLoading = false;
        }
    }
    async retryLastMessage(currentData) {
        const lastMessage = this.chatHistory[this.chatHistory.length - 1];
        if (!lastMessage || lastMessage.messageType !== 'error')
            return;
        const userMessage = this.chatHistory[this.chatHistory.length - 2];
        if (!userMessage || userMessage.role !== 'user')
            return;
        const textToRetry = userMessage.text;
        // Remove the error and the user message so sendChatMessage can re-add them cleanly
        this.chatHistory.splice(this.chatHistory.length - 2, 2);
        // @TODO: FLU-193 Handle this.
        await this.sendChatMessage({ text: textToRetry, currentData });
    }
    // Clear conversation history
    async clearConversationHistory() {
        console.log("Clearing chat history", this.threadId);
        this.chatHistory = [];
        await this.client.clearConversationHistory(this.threadId);
    }
}
